/**
 * Oracle Health Monitor - Admin dashboard widget
 * Shows EMON/USD price oracle status, sources, and alerts
 */

import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  Activity, 
  AlertTriangle, 
  CheckCircle, 
  Clock, 
  RefreshCw, 
  TrendingUp,
  Zap,
  Shield
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

interface OracleData {
  emon_usd_rate: number;
  updated_at: string | null;
  dex_source: string | null;
  sol_to_emon_rate: number | null;
}

export function OracleHealthMonitor() {
  const [oracleData, setOracleData] = useState<OracleData | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  const fetchOracleStatus = async () => {
    try {
      const { data, error } = await supabase
        .from("emon_price_cache")
        .select("emon_usd_rate, updated_at, dex_source, sol_to_emon_rate")
        .eq("id", "current")
        .single();

      if (error) throw error;
      setOracleData(data);
      setLastRefresh(new Date());
    } catch (err) {
      console.error("[OracleMonitor] Failed to fetch:", err);
    } finally {
      setLoading(false);
    }
  };

  const triggerOracleRefresh = async () => {
    setRefreshing(true);
    try {
      const { data, error } = await supabase.functions.invoke("fetch-emon-price");
      if (error) throw error;
      
      toast({
        title: "Oracle Updated",
        description: `New rate: $${data.emonUsdRate?.toFixed(8)} (${data.sourcesUsed} sources)`,
      });
      
      await fetchOracleStatus();
    } catch (err) {
      toast({
        title: "Refresh Failed",
        description: "Could not update oracle price",
        variant: "destructive",
      });
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchOracleStatus();
    const interval = setInterval(fetchOracleStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  const getTimeSinceUpdate = (): number => {
    if (!oracleData?.updated_at) return Infinity;
    return Math.floor((Date.now() - new Date(oracleData.updated_at).getTime()) / 1000);
  };

  const getHealthStatus = () => {
    const age = getTimeSinceUpdate();
    if (age > 300) return { status: "critical", label: "STALE", color: "bg-destructive" };
    if (age > 180) return { status: "warning", label: "AGING", color: "bg-yellow-500" };
    return { status: "healthy", label: "LIVE", color: "bg-green-500" };
  };

  const isClamped = oracleData?.dex_source?.includes("clamped");
  const sourcesCount = oracleData?.dex_source?.match(/median_(.+?)(_|$)/)?.[1]?.split("+").length || 1;
  const health = getHealthStatus();

  if (loading) {
    return (
      <Card className="border-border/50">
        <CardContent className="p-6 flex items-center justify-center">
          <RefreshCw className="w-5 h-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-border/50 bg-card/50 backdrop-blur-sm">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <Activity className="w-5 h-5 text-primary" />
          Oracle Health Monitor
          <Badge className={`${health.color} text-white ml-auto`}>
            {health.label}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Current Rate */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">EMON/USD Rate</p>
            <p className="text-xl font-bold text-foreground">
              ${oracleData?.emon_usd_rate?.toFixed(8) || "N/A"}
            </p>
          </div>
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">1 USD = EMON</p>
            <p className="text-xl font-bold text-foreground">
              {oracleData?.emon_usd_rate 
                ? Math.round(1 / oracleData.emon_usd_rate).toLocaleString()
                : "N/A"}
            </p>
          </div>
        </div>

        {/* Status Indicators */}
        <div className="grid grid-cols-3 gap-2">
          <div className="flex items-center gap-2 p-2 rounded-lg bg-muted/50">
            <Clock className="w-4 h-4 text-muted-foreground" />
            <div>
              <p className="text-[10px] text-muted-foreground">Last Update</p>
              <p className="text-xs font-medium">{getTimeSinceUpdate()}s ago</p>
            </div>
          </div>
          <div className="flex items-center gap-2 p-2 rounded-lg bg-muted/50">
            <TrendingUp className="w-4 h-4 text-muted-foreground" />
            <div>
              <p className="text-[10px] text-muted-foreground">Sources</p>
              <p className="text-xs font-medium">{sourcesCount} active</p>
            </div>
          </div>
          <div className="flex items-center gap-2 p-2 rounded-lg bg-muted/50">
            {isClamped ? (
              <AlertTriangle className="w-4 h-4 text-yellow-500" />
            ) : (
              <CheckCircle className="w-4 h-4 text-green-500" />
            )}
            <div>
              <p className="text-[10px] text-muted-foreground">Clamp</p>
              <p className="text-xs font-medium">{isClamped ? "Active" : "Normal"}</p>
            </div>
          </div>
        </div>

        {/* Source Info */}
        <div className="space-y-1">
          <p className="text-xs text-muted-foreground">Data Source</p>
          <div className="flex flex-wrap gap-1">
            {oracleData?.dex_source?.split("_").filter(s => s && !["median", "single", "clamped"].includes(s)).map((source, i) => (
              <Badge key={i} variant="secondary" className="text-[10px]">
                {source.replace("+", " + ")}
              </Badge>
            ))}
          </div>
        </div>

        {/* Alerts */}
        {health.status !== "healthy" && (
          <div className={`p-3 rounded-lg ${health.status === "critical" ? "bg-destructive/10 border border-destructive/30" : "bg-yellow-500/10 border border-yellow-500/30"}`}>
            <div className="flex items-start gap-2">
              <AlertTriangle className={`w-4 h-4 ${health.status === "critical" ? "text-destructive" : "text-yellow-500"}`} />
              <div>
                <p className="text-sm font-medium">
                  {health.status === "critical" ? "Stale Price Data" : "Price Data Aging"}
                </p>
                <p className="text-xs text-muted-foreground">
                  {health.status === "critical" 
                    ? "Transactions will be rejected. Refresh oracle immediately."
                    : "Consider refreshing to get latest rates."}
                </p>
              </div>
            </div>
          </div>
        )}

        {isClamped && (
          <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
            <div className="flex items-start gap-2">
              <Shield className="w-4 h-4 text-yellow-500" />
              <div>
                <p className="text-sm font-medium">Anti-Manipulation Active</p>
                <p className="text-xs text-muted-foreground">
                  Price change exceeded ±5% limit and was clamped for protection.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Refresh Button */}
        <Button 
          variant="outline" 
          size="sm" 
          className="w-full gap-2"
          onClick={triggerOracleRefresh}
          disabled={refreshing}
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
          {refreshing ? "Updating..." : "Force Refresh Oracle"}
        </Button>
      </CardContent>
    </Card>
  );
}
